import { Box, Button, DataTable, IconButton, Stack } from '@chronosphereio/chrono-ui';
import { PencilOutline as Pencil, TrashCanOutline as TrashCan } from 'mdi-material-ui';
import { useMemo, useState } from 'react';
import { ContextLinkDefinition, parseTemplateVariables } from '@/core';
import { OptionsEditorGroup } from '@/plugin-editor-components';
import { ContextLinkConfig, getContextLinkType } from '../../../model';
import { ContextLinkEditor as LinkEditorComponent } from '../../../runtime/ContextLinkTypesProvider';

type ContextLinkEditorProps = {
  links: ContextLinkDefinition[];
  onLinksChange: (links: ContextLinkDefinition[]) => void;
  contextLinkConfig?: ContextLinkConfig;
};

export function ContextLinkEditor({ links = [], onLinksChange, contextLinkConfig }: ContextLinkEditorProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const linksData = useMemo(
    () =>
      links.map((l, idx) => {
        return {
          id: idx,
          title: l.spec.title,
          url: l.spec.url,
          type: getContextLinkType(l.spec.url, contextLinkConfig),
          parameters: parseTemplateVariables(l.spec.url),
        };
      }),
    [links, contextLinkConfig]
  );

  let editingLink: ContextLinkDefinition | undefined;
  if (editingIdx !== null) {
    editingLink = links[editingIdx];
  }

  const removeLinkByIndex = (idx: number) => {
    const newLinks = [...links];
    newLinks.splice(idx, 1);
    onLinksChange?.(newLinks);
  };

  return (
    <div>
      {editingIdx === null && (
        <div>
          <DataTable
            autoHeight
            localeText={{
              noRowsLabel: 'No links',
            }}
            sx={{
              mb: 1,
            }}
            hideFooter
            rows={linksData}
            getRowId={(row) => row.id}
            columns={[
              {
                field: 'title',
                headerName: 'Name',
              },
              {
                flex: 1,
                field: 'url',
                headerName: 'URL',
              },
              {
                width: 200,
                field: 'parameters',
                headerName: 'Parameters',
                renderCell: (params) => {
                  if (params.value.length === 0) return '--';
                  return params.value.join(', ');
                },
              },
              {
                field: 'type',
                headerName: 'Type',
              },
              {
                sortable: false,
                align: 'right',
                field: 'actions',
                renderHeader() {
                  return <></>;
                },
                renderCell: ({ row }) => {
                  return (
                    <Stack spacing={1} direction="row">
                      <IconButton
                        onClick={() => {
                          setEditingIdx(row.id);
                        }}
                      >
                        <Pencil />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          removeLinkByIndex(row.id);
                        }}
                      >
                        <TrashCan />
                      </IconButton>
                    </Stack>
                  );
                },
              },
            ]}
          />
          <Button variant="secondary" onClick={() => setEditingIdx(-1)}>
            Add link
          </Button>
        </div>
      )}
      {editingIdx !== null && (
        <OptionsEditorGroup title="Add a Link">
          <Box mt={1} width="100%">
            <LinkEditorComponent
              onSave={(newLink) => {
                const newLinks = [...links];

                if (editingIdx === -1) {
                  newLinks.push(newLink);
                } else {
                  newLinks[editingIdx] = newLink;
                }
                onLinksChange?.(newLinks);
                setEditingIdx(null);
              }}
              onCancel={() => {
                setEditingIdx(null);
              }}
              saveLabel="Save link"
              cancelLabel="Cancel"
              initialLink={editingLink}
              contextLinkConfig={contextLinkConfig}
            />
          </Box>
        </OptionsEditorGroup>
      )}
    </div>
  );
}
